import liff from '@line/liff'

import { callApi, ApiService } from './api'
import { MessageID } from '../constants/lineMessage'
import { useState, useEffect } from 'react'
import { useI18next } from 'gatsby-plugin-react-i18next'
import { getUserLanguageSetting, getUsersCollection } from './firestore'
import { Language } from '../constants/i18n'

interface UserProfile {
  userId: string
  displayName: string
  pictureUrl?: string
}

const mockedProfile = {
  userId: 'U5d8246b80fd96e946d92bcf287e44948', //new Date().getTime().toString(),
  displayName: 'Just for Dev',
}

export class Liff {
  private userProfile = null

  init(): Promise<void> {
    return liff.init({
      liffId: process.env.GATSBY_LIFF_ID,
    })
  }

  isLoggedInInClient(): boolean {
    return liff.isInClient() && liff.isLoggedIn()
  }

  async getUserProfile(): Promise<UserProfile> {
    if (!this.userProfile) {
      if (this.isLoggedInInClient()) {
        this.userProfile = await liff.getProfile()
      } else if (process.env.GATSBY_ENVIRONMENT === 'development') {
        this.userProfile = mockedProfile
      }
    }

    return this.userProfile
  }

  async sendLineMessageAndClose(messageId: MessageID): Promise<void> {
    const userProfile = await this.getUserProfile()

    if (userProfile) {
      await callApi({
        service: ApiService.message,
        body: {
          token: process.env.GATSBY_API_MESSAGE_TOKEN,
          userId: userProfile.userId,
          messageId,
        },
      })

      liff.closeWindow()
    }
  }
}

type UseLiffData = {
  liff: any
  profile: any
  loading: boolean
  error: any
  userLanguage: Language
}

export function useLiff({
  loadLanguage = false,
  notAllowRegisteredUser = false,
}: {
  loadLanguage?: boolean
  notAllowRegisteredUser?: boolean
} = {}): UseLiffData {
  const [loading, setLoading] = useState(true)
  const [liff, setLiff] = useState(null)
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [userLanguage, setUserLanguage] = useState(null)
  const { changeLanguage, language } = useI18next()

  useEffect(() => {
    async function initLiff() {
      try {
        const liffInstance = new Liff()
        await liffInstance.init()
        const fetchedProfile = await liffInstance.getUserProfile()

        if (
          notAllowRegisteredUser &&
          (await isUserAlreadyRegistered(fetchedProfile.userId))
        ) {
          await liffInstance.sendLineMessageAndClose(MessageID.registerAlready)
          return
        }

        if (loadLanguage) {
          const userLanguage = await getUserLanguageSetting(
            fetchedProfile.userId,
          )

          setUserLanguage(userLanguage)

          if (userLanguage !== language) {
            await changeLanguage(userLanguage)
            return
          }
        }

        setLiff(liffInstance)
        setProfile(fetchedProfile)
        setLoading(false)
      } catch (error) {
        setError(error.message || error)
        setLoading(false)
      }
    }

    initLiff()
  }, [])

  return {
    liff,
    profile,
    loading,
    error,
    userLanguage,
  }
}

async function isUserAlreadyRegistered(userId: string): Promise<boolean> {
  const userRecord = (await getUsersCollection().doc(userId).get()).data()
  return userRecord && userRecord.isRegistered
}
